import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { Button } from "../../../Components/Buttons";
import {
  FormInput,
  FormSection,
  FormSwitch,
} from "../../../Components/Form";

import { ToastMessage } from "../../../Components/Utils";
import BreadCrumb from "../../../Components/BreadCrumb";
import { ROUTE_PATHS } from "../../../constants/routeConstants";
import { handleError } from "../../../stores/components/handleError";
import { Trash2Icon } from "lucide-react";
import { useCreateFAQMutation, useUpdateFAQMutation } from "../../../stores/api/faqs.api";

const EMPTY_FAQ = {
  question: "",
  answer: "",
};

const EMPTY = {
  section_name: "",
  faqs: [EMPTY_FAQ],
  status: 1,
};

const CreateFAQs = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const details = location?.state?.faq;

  const [createFAQS, { isLoading: creating }] = useCreateFAQMutation();
  const [updateFAQs, { isLoading: updating }] = useUpdateFAQMutation();

  const [formData, setFormData] = useState(
    details
      ? {
        section_name: details.section_name || "",
        status: details.status ? 1 : 0,
        faqs:
          details.faqs?.length > 0
            ? details.faqs.map((faq) => ({
              question: faq.question || "",
              answer: faq.answer || "",
              status: faq.status ? 1 : 0,
            }))
            : [
              {
                question: "",
                answer: "",
                status: 1,
              },
            ],
      }
      : EMPTY
  );

  // =========================
  // HANDLE FAQ CHANGE
  // =========================
  const handleFAQChange = (index, field, value) => {
    const updatedFAQs = [...formData.faqs];

    updatedFAQs[index][field] = value;

    setFormData((prev) => ({
      ...prev,
      faqs: updatedFAQs,
    }));
  };

  const addFAQ = () => {
    setFormData((prev) => ({
      ...prev,
      faqs: [
        ...prev.faqs,
        {
          question: "",
          answer: "",
        },
      ],
    }));
  };

  const removeFAQ = (index) => {
    const updatedFAQs = [...formData.faqs];

    updatedFAQs.splice(index, 1);

    setFormData((prev) => ({
      ...prev,
      faqs:
        updatedFAQs.length > 0
          ? updatedFAQs
          : [EMPTY_FAQ],
    }));
  };

  const resetForm = () => {
    setFormData(EMPTY);
  };

  const onSubmit = async (e) => {
    e.preventDefault();

    try {
      const payload = {
        ...formData,
        status: formData.status ? 1 : 0,
      };

      if (details) {
        
        await updateFAQs({ id: details.id, body: payload }).unwrap();
      }
      else {
        await createFAQS(payload).unwrap();
      }

      ToastMessage({
        type: "success",
        title: "Success",
        message: details
          ? "FAQs Updated Successfully"
          : "FAQs Created Successfully",
      });

      if (details) {
        navigate(location.pathname, {
          replace: true,
          state: {
            faq: {
              ...payload,
              id: details.id,
            },
          },
        });
      } else {
        resetForm();
      }
    } catch (err) {
      handleError(err, "Something went wrong");
    }
  };

  return (
    <div className="p-4">
      <BreadCrumb
        title={
          details
            ? "Edit FAQs"
            : "Create FAQs"
        }
      />

      <form
        onSubmit={onSubmit}
        className="mx-auto mt-6 dark:bg-slate-900 rounded-lg p-6 space-y-6"
      >
        {/* ========================= */}
        {/* SECTION TITLE */}
        {/* ========================= */}

        <FormSection title="FAQ Section">
          <div className="grid grid-cols-1 gap-4">
            <FormInput
              label="Section Title"
              name="section_name"
              value={formData.section_name}
              isRequired
              onChange={(val) =>
                setFormData((prev) => ({
                  ...prev,
                  section_name: val,
                }))
              }
              placeholder="Enter section title"
            />
          </div>
        </FormSection>
        <FormSection title="Questions & Answers">
          <div className="flex justify-end item-center">
            <Button
              type="button"
              onClick={addFAQ}
              className="!bg-green-500 !text-white hover:!bg-green-600"
            >
              + Add More
            </Button>
          </div>
          <div className="space-y-5">
            {formData.faqs.map((faq, index) => (
              <div
                key={index}
                className="border border-gray-200 dark:border-slate-700 rounded-xl p-4 space-y-4"
              >
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-lg">
                    FAQ #{index + 1}
                  </h3>

                  {formData.faqs.length > 1 && (
                    <Button
                      type="button"
                      onClick={() => removeFAQ(index)}
                      className="!bg-red-500 !text-white hover:!bg-red-600"
                    >
                      <Trash2Icon />
                    </Button>
                  )}
                </div>

                <FormInput
                  label="Question"
                  value={faq.question}
                  isRequired
                  onChange={(val) =>
                    handleFAQChange(
                      index,
                      "question",
                      val
                    )
                  }
                  placeholder="Enter question"
                />

                <FormInput
                  label="Answer"
                  value={faq.answer}
                  isRequired
                  rows={4}
                  onChange={(val) =>
                    handleFAQChange(
                      index,
                      "answer",
                      val
                    )
                  }
                  placeholder="Enter answer"
                />
              </div>
            ))}

            {/* ADD BUTTON */}


          </div>
        </FormSection>

        {/* ========================= */}
        {/* STATUS */}
        {/* ========================= */}

        <div>
          <FormSwitch
            label="Status"
            checked={formData.status}
            onChange={(val) =>
              setFormData((prev) => ({
                ...prev,
                status: val ? 1 : 0,
              }))
            }
          />
        </div>

        {/* ========================= */}
        {/* ACTION BUTTONS */}
        {/* ========================= */}

        <div className="flex items-center justify-end gap-3 pt-2">
          <Button
            type="button"
            disabled={creating || updating}
            onClick={() => {
              resetForm();

              navigate(
                ROUTE_PATHS.FAQS_LIST || "/faqs"
              );
            }}
            className="!bg-gray-100 !text-gray-700 hover:!bg-gray-200 dark:!bg-slate-800 dark:!text-gray-200"
          >
            Cancel
          </Button>

          <Button
            type="submit"
            isLoading={creating || updating}
            disabled={creating || updating}
            className="!bg-green-500 !text-white hover:!bg-green-600"
            loadingText={
              details
                ? "Updating..."
                : "Creating..."
            }
          >
            {details
              ? "Update FAQs"
              : "Create FAQs"}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default CreateFAQs;